import React, { useEffect, useState } from "react";
import { Bell, BellOff, Mail, MailX, Send, AlertCircle, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  pushSupported,
  getCurrentPushSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/lib/push";

const Toggle = ({ checked, onChange, testId }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-[#FF2E00]" : "bg-white/15"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const Notifications = () => {
  const { user, login } = useAuth();
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [pushActive, setPushActive] = useState(false); // browser-level subscription exists
  const [busy, setBusy] = useState(false);
  const [supported] = useState(pushSupported());

  useEffect(() => {
    if (!user) return;
    api.get("/notifications/preferences").then((r) => {
      setEmailEnabled(r.data?.email_enabled ?? true);
      setPushEnabled(r.data?.push_enabled ?? true);
    }).catch(() => {});
    if (supported) {
      getCurrentPushSubscription().then((sub) => setPushActive(!!sub)).catch(() => {});
    }
  }, [user, supported]);

  const savePrefs = async (next) => {
    setEmailEnabled(next.email_enabled);
    setPushEnabled(next.push_enabled);
    try {
      await api.put("/notifications/preferences", next);
    } catch {
      toast.error("Failed to save preferences");
    }
  };

  const handleEnablePush = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setPushActive(true);
      await savePrefs({ email_enabled: emailEnabled, push_enabled: true });
      toast.success("Push notifications enabled");
    } catch (e) {
      toast.error(e?.message || "Could not enable push");
    } finally {
      setBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setPushActive(false);
      toast.success("Push notifications disabled");
    } catch {
      toast.error("Failed to unsubscribe");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/notifications/test");
      const parts = [];
      if (r.data?.email) parts.push("email");
      if ((r.data?.push || 0) > 0) parts.push(`${r.data.push} push device(s)`);
      toast.success(parts.length ? `Sent test via ${parts.join(" + ")}` : "Sent (check console)");
    } catch {
      toast.error("Test failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="px-4 sm:px-8 py-12 max-w-2xl mx-auto text-center">
        <Bell size={36} className="mx-auto text-white/40 mb-4" />
        <h1 className="font-display text-3xl font-semibold mb-2">Sign in to manage notifications</h1>
        <p className="text-white/60 mb-6">Email + push alerts when artists you track announce new releases.</p>
        <button
          onClick={login}
          className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.03] transition"
          data-testid="notifications-signin-btn"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div data-testid="notifications-page" className="px-4 sm:px-8 py-6 max-w-3xl mx-auto">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#E2FF31] font-bold mb-2 flex items-center gap-1.5">
          <Bell size={12} /> Notifications
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-2">
          Stay in the loop.
        </h1>
        <p className="text-white/60">
          We&apos;ll only ping you when an artist you&apos;re tracking confirms a release date.
        </p>
      </div>

      {/* Email card */}
      <div className="rounded-2xl bg-white/[0.04] border border-white/8 p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#FF2E00]/15 flex items-center justify-center shrink-0">
              <Mail size={18} className="text-[#FF2E00]" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-base">Email alerts</h3>
              <p className="text-sm text-white/55">
                Sent to <span className="text-white">{user.email}</span> when a tracked artist&apos;s
                release date is confirmed.
              </p>
            </div>
          </div>
          <Toggle
            checked={emailEnabled}
            onChange={(v) => savePrefs({ email_enabled: v, push_enabled: pushEnabled })}
            testId="notifications-email-toggle"
          />
        </div>
      </div>

      {/* Push card */}
      <div className="rounded-2xl bg-white/[0.04] border border-white/8 p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#FF2E00]/15 flex items-center justify-center shrink-0">
              <Bell size={18} className="text-[#C99CFF]" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-base">Push notifications</h3>
              <p className="text-sm text-white/55">
                {!supported
                  ? "Your browser doesn't support web push."
                  : pushActive
                  ? "Enabled on this device."
                  : "Get an alert on your device the moment a release is confirmed."}
              </p>
            </div>
          </div>
          {!supported ? (
            <span className="text-xs text-white/40">Unsupported</span>
          ) : pushActive ? (
            <button
              onClick={handleDisablePush}
              disabled={busy}
              data-testid="notifications-push-disable"
              className="text-xs font-semibold rounded-full px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 transition flex items-center gap-1.5 disabled:opacity-50"
            >
              <BellOff size={14} /> Disable
            </button>
          ) : (
            <button
              onClick={handleEnablePush}
              disabled={busy}
              data-testid="notifications-push-enable"
              className="text-xs font-semibold rounded-full px-4 py-2 bg-[#FF2E00] hover:bg-[#E62900] text-white transition flex items-center gap-1.5 disabled:opacity-50"
            >
              <Bell size={14} /> Enable
            </button>
          )}
        </div>

        {pushActive && (
          <div className="mt-4 pl-13 flex items-center justify-between">
            <p className="text-sm text-white/55">In-app preference</p>
            <Toggle
              checked={pushEnabled}
              onChange={(v) => savePrefs({ email_enabled: emailEnabled, push_enabled: v })}
              testId="notifications-push-toggle"
            />
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-6 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-sm mb-0.5">Send a test</h3>
            <p className="text-xs text-white/55">
              Fire a sample &ldquo;new release confirmed&rdquo; alert to your enabled channels.
            </p>
          </div>
          <button
            onClick={sendTest}
            disabled={busy}
            data-testid="notifications-test-btn"
            className="text-xs font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/15 text-white transition flex items-center gap-1.5 disabled:opacity-50"
          >
            <Send size={14} /> Send test
          </button>
        </div>
      </div>

      <div className="text-xs text-white/40 leading-relaxed flex gap-2 px-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <span>
          Notifications fire only on the FIRST detection of a confirmed release date — we&apos;ll
          never spam you with rumors.
        </span>
      </div>
    </div>
  );
};

export default Notifications;
